/**
 * The drifting skyline behind the sign-in panel.
 *
 * A flat green rectangle is a lot of nothing on a widescreen monitor. These are the same property
 * marks the rest of the app uses — a shop, a terrace, a warehouse, an event centre — scattered
 * across it and drifting on their own timings, which says what the product is about before a word
 * is read.
 *
 * **Deliberately not random.** `Math.random()` would place them differently on every render, so a
 * re-render would make the whole backdrop jump. The placements are a fixed table: chosen once,
 * spread so nothing clusters, and stable for ever.
 *
 * It is decorative and inert — `aria-hidden`, no pointer events, and nothing behind it can be
 * clicked through by accident. `prefers-reduced-motion` stops the drift in the stylesheet; the
 * icons stay where they are rather than disappearing, because the composition is the point and
 * the motion is the flourish.
 */

import { PropertyTypeIcon } from "./property-icons";

interface Placement {
  type: string;
  /** Percentages of the panel, so the composition holds at any width. */
  top: number;
  left: number;
  size: number;
  /**
   * Seconds. Different for each, so they never fall into step with one another.
   *
   * Around ten to fifteen. Faster than this and the panel is busy — the eye keeps being pulled
   * off the form; slower and it looks like nothing is moving at all.
   */
  duration: number;
  /** Which of the three routes it takes. */
  path: "a" | "b" | "c";
  delay: number;
  opacity: number;
}

/**
 * Twelve marks, spread by hand.
 *
 * Sizes and opacities are paired on purpose: the big ones are the faintest, so they read as
 * distance rather than as something shouting from the back of the panel.
 *
 * They are also kept **out of the column the copy occupies** — roughly 3% to 55% across, 30% to
 * 85% down. A mark behind a paragraph is not texture, it is something the eye keeps trying to
 * resolve while reading.
 */
const PLACEMENTS: Placement[] = [
  // Above the copy
  { type: "block_of_flats", top: 6, left: 5, size: 64, duration: 12.1, delay: 0, opacity: 0.1, path: "a" },
  { type: "detached", top: 4, left: 42, size: 38, duration: 9.6, delay: 3.1, opacity: 0.15, path: "b" },
  { type: "land", top: 13, left: 87, size: 34, duration: 8.1, delay: 4.2, opacity: 0.17, path: "c" },

  // The right-hand margin, which the text never reaches
  { type: "shop", top: 24, left: 70, size: 44, duration: 8.8, delay: 1.4, opacity: 0.16, path: "a" },
  { type: "terrace", top: 40, left: 76, size: 92, duration: 15.3, delay: 0.6, opacity: 0.06, path: "b" },
  { type: "office", top: 56, left: 88, size: 58, duration: 12.9, delay: 1.8, opacity: 0.1, path: "c" },
  { type: "event_centre", top: 72, left: 68, size: 72, duration: 13.6, delay: 0.9, opacity: 0.08, path: "a" },
  { type: "mixed_use", top: 62, left: 58, size: 48, duration: 14.6, delay: 0.3, opacity: 0.09, path: "b" },

  // Below it
  { type: "warehouse", top: 90, left: 5, size: 52, duration: 10.5, delay: 2.2, opacity: 0.13, path: "c" },
  { type: "duplex", top: 87, left: 30, size: 42, duration: 11.3, delay: 2.6, opacity: 0.14, path: "a" },
  { type: "self_contained", top: 95, left: 46, size: 32, duration: 10.1, delay: 3.6, opacity: 0.16, path: "b" },
  { type: "bungalow", top: 91, left: 82, size: 40, duration: 12.6, delay: 1.1, opacity: 0.12, path: "c" },
];

export function AuthBackdrop() {
  return (
    <div aria-hidden="true" className="auth-backdrop pointer-events-none absolute inset-0 overflow-hidden">
      {PLACEMENTS.map((placement, index) => (
        <span
          key={index}
          className={`auth-drift auth-drift-${placement.path} absolute text-brand-300`}
          style={{
            top: `${placement.top}%`,
            left: `${placement.left}%`,
            opacity: placement.opacity,
            animationDuration: `${placement.duration}s`,
            animationDelay: `-${placement.delay}s`,
          }}
        >
          <PropertyTypeIcon type={placement.type} size={placement.size} />
        </span>
      ))}
    </div>
  );
}
